import {
  IonApp,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonToolbar
} from "@ionic/react";
import { homeOutline, logInOutline, logOutOutline, personCircleOutline } from "ionicons/icons";
import { useEffect, useState } from "react";
import type { SpaNavigationItem, SpaShellProps } from "../types";
import { AppTitle, shortenAccountName } from "./AppTitle";

export function SpaShell({ children, contentClassName, navigation, themeClassName, endControls }: SpaShellProps) {
  const [selectedAppSlug, setSelectedAppSlug] = useState(navigation.appSlug);
  const navigationItems: SpaNavigationItem[] = [
    { label: "Home", value: "home", url: navigation.portalUrl },
    { label: "Spending Tracker", value: "spendtrack", url: navigation.trackerUrl },
    { label: "BMI Calculator", value: "bmi", url: navigation.bmiUrl }
  ];

  useEffect(() => {
    function syncSelectedApp() {
      setSelectedAppSlug(navigation.appSlug);
    }

    syncSelectedApp();
    window.addEventListener("pageshow", syncSelectedApp);

    return () => window.removeEventListener("pageshow", syncSelectedApp);
  }, [navigation.appSlug]);

  function logout() {
    document.querySelector<HTMLFormElement>("#spa-logout-form")?.submit();
  }

  function navigateTo(value: string | null | undefined) {
    const item = navigationItems.find((candidate) => candidate.value === value);

    if (item) {
      setSelectedAppSlug(item.value);
    }

    if (item && item.value !== navigation.appSlug) {
      window.location.assign(item.url);
    }
  }

  return (
    <IonApp className={themeClassName}>
      <IonPage>
        <IonHeader translucent={true} className="spa-header">
          <IonToolbar>
            <a className="toolbar-title" href={navigation.portalUrl} aria-label="Open Pyeah home">
              <AppTitle title={navigation.applicationTitle} />
            </a>
            <IonButtons slot="end">
              <IonButton className="spa-home-button" fill="clear" size="small" href={navigation.portalUrl} aria-label="Open Pyeah home">
                <IonIcon slot="icon-only" icon={homeOutline} />
              </IonButton>
              <IonSelect
                className="spa-app-switcher"
                aria-label="Open another Pyeah app"
                interface="popover"
                selectedText="Apps"
                value={selectedAppSlug}
                onIonChange={(event) => navigateTo(String(event.detail.value))}
              >
                {navigationItems.map((item) => (
                  <IonSelectOption value={item.value} key={item.value}>
                    {item.label}
                  </IonSelectOption>
                ))}
              </IonSelect>
              <div className="account-toolbar" aria-label="Account menu">
                {navigation.isAuthenticated ? (
                  <>
                    <IonButton
                      className="account-chip-button"
                      fill="clear"
                      href={navigation.accountManageUrl}
                      title={navigation.userDisplayName}
                      aria-label="Account"
                    >
                      <IonIcon icon={personCircleOutline} aria-hidden="true" />
                      <span>{shortenAccountName(navigation.userDisplayName)}</span>
                    </IonButton>
                    <IonButton className="logout-button" fill="clear" size="small" aria-label="Log out" onClick={logout}>
                      <IonIcon slot="start" icon={logOutOutline} />
                      <span>Log out</span>
                    </IonButton>
                  </>
                ) : (
                  <IonButton fill="clear" size="small" href={navigation.accountLoginUrl} aria-label="Log in">
                    <IonIcon slot="start" icon={logInOutline} />
                    <span>Log in</span>
                  </IonButton>
                )}
              </div>
              {endControls}
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent fullscreen={true} scrollY={true} className={contentClassName}>
          {children}
        </IonContent>
      </IonPage>
    </IonApp>
  );
}
